/**
* @module contexts/LanguageProvider
* @author Charles Blais <charles.blais@canada.ca>
*
* NOTE: not all language text is found in here unfortunatly, some are also generated
* from the components.  The main reason is because the text is embedded with a variable
* calculated from within the component.
* NOTE2: the API page text is only found in the api component since its too much for this object
*/

import React, { createContext, useContext, useReducer } from "react";

/**
 * Language state States
 * ---------------------------
 */
type State = string | JSX.Element

export type LanguageState = {
  abbr: string,
  title: string,
  codeMap: { [key: string]: string },
  modelMap: { [key: string]: string },
  modelNotice: State,
  goback: State,
  feedback: {
    error: string,
    errorDetails: string,
    loading: string,
    loadingDetails: string,
    empty: string,
    emptyDetails: string,
  },
  form: {
    help: string,
    currentLocation: string,
    address: string,
    searchAddress: string,
    setCoordinates: string,
    code: string,
    coordinates: string,
    coordinatesAddress: string,
    coordinatesAddressHelp: string,
    coordinatesCurrent: string,
    coordinatesManual: string,
    coordinatesInstructions: State,
    coodinatesManualInstructions: string,
    siteDesignation: string,
    siteDesignationHelpMap: { [key: string]: State },
    mapConfirm: string,
    submit: string,
    beforeSubmit: string,
    selectedCoord: string,
    siteDesginationLabels: { [key: string]: State },
    // range of XV should match the one in AppState
    siteDesignationXV: State,
    siteDesignationXS: string,
    error: {
      latitude: string,
      longitude: string,
      siteDesignationXS: string,
      siteDesignationXV: string,
      location: string,
    }
  },
  summary: {
    details: string,
    model: string,
    siteDesignationXV: State,
    siteDesignationXS: State,
  },
  chart: {
    uhs: string,
    hazardCurve: string,
    vs30: string,
    sa: string,
    gm: string,
    siteAmplification: string,
    probabilitySelect: string,
    probability: string,
    period: string,
    downloadImage: string,
    zoom: string,
    back: string,
    restore: string,
  },
  table: {
    all: string,
    siteDesignation: string,
    poe50: string,
    poe1: string,
    pga: string,
    pgv: string,
    download: string,
    nbcTitle2: string,
    nbcTitle5: string,
    nbcTitle10: string,
    search: string,
    records: string,
  },
  tab: {
    intro: State,
    additional: string,
    tables: string,
    tablesDetailNBC: State,
    tablesDetail: State,
    nbcDetailMap: { [key: string]: State },
    nbcCollapse: string,
    plots: string,
    plotsDetail: State,
    maps: string,
    api: string,
    information: string,
  },
  leaflet: {
    url: string,
    attribution: string,
  },
}


export const initialStateEnglish: LanguageState = {
  abbr: 'en',
  title: 'Seismic Hazard Values',
  codeMap: {
    'nbc2020': 'NBC 2020',
    'nbc2025': 'NBC 2025',
  },
  modelMap: {
    'nbc2020': 'CanadaSHM6-v1',
    'nbc2025': 'CanadaSHM6-v1',
  },
  modelNotice: <>Older editions are available within the <a href="/hazard-alea/interpolat/calc-en.php">2015-2005 hazard calculator</a></>,
  goback: <>Go back to the <a href={window.location.pathname}>seismic hazard calculator form</a></>,
  feedback: {
    error: 'Error!',
    errorDetails: 'There was an error when loading the results.  Refresh your web browser and if the problem persists, please let us know.',
    loading: 'Loading...',
    loadingDetails: 'We are loading the results, please wait.',
    empty: 'No seismic hazard model found.',
    emptyDetails: 'Your selected coordinates are outside of Canada. Please revise your coordinates to be within Canada and try again.',
  },
  form: {
    help: 'Help',
    currentLocation: 'Estimate my current location',
    address: 'Address',
    searchAddress: 'Search address',
    setCoordinates: 'Set coordinates',
    code: 'Code edition',
    coordinates: 'Location',
    coordinatesAddress: 'Address',
    coordinatesAddressHelp: 'ex: 275 Notre-Dame Street East, Montreal, Quebec',
    coordinatesCurrent: 'Current Location',
    coordinatesManual: 'Latitude and Longitude',
    coordinatesInstructions: (
      <details>
        <summary>Instructions</summary>
        <p>Seismic hazard values are calculated for specific geographical coordinates.  This tool can assist you in determining the coordinates of your site using one of the following options.</p>
        <ul>
          <li><strong>Address</strong>: the street address of the site</li>
          <li><strong>Current Location</strong>: your current location can be estimated using the geolocation data from your browser</li>
          <li><strong>Manually</strong>: by entering the latitude (between 42 and 90) and longitude (between -141 and -45) of the site in decimal degree format</li>
        </ul>
        <p>Once the Search address button is selected, a map showing the user's chosen location will appear. The user can adjust the location on the map as needed.</p>
      </details>
    ),
    coodinatesManualInstructions: 'Latitude (between 42 and 90) and longitude (between -141 and -45) of the site in decimal degree format with a maximum 3 decimals.',
    siteDesignation: 'Site designation',
    siteDesignationHelpMap: {
      'nbc2020': <>Select the site designation in accordance with Article 4.1.8.4. of the NBC 2020. Further guidance is available in the Commentary entitled <em>Design for Seismic Effects</em> in the <em>Structural Commentaries (User's Guide - NBC 2020: Part 4 of Division B).</em></>,
      'nbc2025': <>Select the site designation in accordance with Article 4.1.8.4. of the NBC 2025. Further guidance is available in the Commentary entitled <em>Design for Seismic Effects</em> in the <em>Structural Commentaries (User's Guide - NBC 2025: Part 4 of Division B).</em></>,
    },
    mapConfirm: 'Please confirm the location.  You may manually adjust the location by moving the marker.',
    submit: 'Obtain Seismic Hazard Values',
    beforeSubmit: 'Before submitting, please make sure that the following are corrected:',
    selectedCoord: 'Selected coordinates are',
    siteDesginationLabels: {
      XV: <>V<sub>s30</sub> (X<sub>V</sub>)</>,
      XS: <>Site Class (X<sub>S</sub>)</>,
    },
    // range of XV should match the one in AppState
    siteDesignationXV: <>V<sub>s30</sub> value measured in situ between 140 - 3000 <abbr title="meters per second">m/s</abbr></>,
    siteDesignationXS: 'Site Class',
    error: {
      latitude: 'Latitude must be between 42 and 90 degrees in decimal degrees format.',
      longitude: 'Longitude must be between -141 and -45 degrees in decimal degrees format.',
      siteDesignationXS: 'For a site designation of X\u209B, a Site Class must be selected.',
      siteDesignationXV: 'For a site designation of X\u1D65, Vs30 must be set and between 140 and 3000 m/s.',
      location: 'Unable to find the coordinates of the address.  Please correct your address or use one of the other options.',
    }
  },
  summary: {
    details: 'User requested values',
    model: 'Model',
    siteDesignationXV: <>Site designation X<sub>V</sub></>,
    siteDesignationXS: <>Site designation X<sub>S</sub></>,
  },
  chart: {
    uhs: 'Uniform Hazard Spectra',
    hazardCurve: 'Hazard Curve',
    vs30: 'Vs30 (m/s)',
    sa: 'Spectral Accelaration (g)',
    gm: '5%-damped Spectral Acceleration (g)',
    siteAmplification: 'Site Amplification',
    probabilitySelect: 'Probability',
    probability: 'Annual Probability of Exceedance',
    period: 'Period (s)',
    downloadImage: 'Download image in PNG format',
    zoom: 'area zoom',
    back: 'previous area zoom',
    restore: 'restore area zoom',
  },
  table: {
    all: 'All',
    siteDesignation: 'Site Designation',
    poe50: 'Probability',
    poe1: 'Probability',
    pga: 'PGA',
    pgv: 'PGV',
    download: 'Download CSV',
    nbcTitle2: '2%/50 years (0.000404 per annum) probability',
    nbcTitle5: '5%/50 years (0.001 per annum) probability',
    nbcTitle10: '10%/50 years (0.0021 per annum) probability',
    search: 'Search',
    records: 'records',
  },
  tab: {
    intro: (
      <>
        <p><strong>Please select one of the tabs below.</strong></p>
      </>
    ),
    additional: 'Additional Values',
    tables: 'CanadaSHM',
    tablesDetailNBC: <p><strong>The NBC 5% damped spectral acceleration values can be viewed in the NBC tab. Additional hazard values for your site can be found below.</strong></p>,
    tablesDetail: (
      <>
        <p>The 5%-damped spectral acceleration (S<sub>a</sub>(T), where T is the period, in s) and peak ground acceleration (PGA) values are given in units of acceleration due to gravity (g, 9.81 m/s<sup>2</sup>).  Peak ground velocity (PGV) is given in m/s.  Probability is expressed in terms of percent (%) exceedance in 50 years.</p>
        <p>By default, all probabilities for the user-specified site designation are shown. Other site designations can be selected from the respective drop-down menu in the table. In low hazard regions, a minimum value of 0.001g for T &le; 2.0s and of 0.0001g for T &gt; 2.0s is assigned. Further information on the calculation of seismic hazard is provided in the <em>Background Information</em> tab.</p>
      </>
    ),
    nbcDetailMap: {
      "nbc2020": (
        <>
          <p>The 5%-damped <u>spectral acceleration</u> (S<sub>a</sub>(T,X), where T is the period, in s, and X is the site designation) and <u>peak ground acceleration</u> (PGA(X)) values are given in units of acceleration due to gravity (g, 9.81 m/s<sup>2</sup>). <u>Peak ground velocity</u> (PGV(X)) values are given in m/s. Probability is expressed in terms of percent exceedance in 50 years. Further information on the calculation of seismic hazard is provided under the <em>Background Information</em> tab.</p>
          <p>The 2%-in-50-year seismic hazard values are provided in accordance with Article 4.1.8.4. of the NBC 2020. The 5%- and 10%-in-50-year values are provided for additional performance checks in accordance with Article 4.1.8.23. of the NBC 2020.</p>
          <p>See the <em>Additional Values</em> tab for additional seismic hazard values, including values for other site designations, periods, and probabilities not defined in the NBC 2020.</p>
        </>
      ),
      "nbc2025": (
        <>
          <p>The 5%-damped <u>spectral acceleration</u> (S<sub>a</sub>(T,X), where T is the period, in s, and X is the site designation) and <u>peak ground acceleration</u> (PGA(X)) values are given in units of acceleration due to gravity (g, 9.81 m/s<sup>2</sup>). <u>Peak ground velocity</u> (PGV(X)) values are given in m/s. Probability is expressed in terms of percent exceedance in 50 years. Further information on the calculation of seismic hazard is provided under the <em>Background Information</em> tab.</p>
          <p>The 2%-in-50-year seismic hazard values are provided in accordance with Article 4.1.8.4. of the NBC 2025. The 5%- and 10%-in-50-year values are provided for additional performance checks in accordance with Article 4.1.8.23. of the NBC 2025.</p>
          <p>See the <em>Additional Values</em> tab for additional seismic hazard values, including values for other site designations, periods, and probabilities not defined in the NBC 2025.</p>
        </>
      ),
    },
    nbcCollapse: 'Tables for 5% and 10% in 50 year values',
    plots: 'Plots',
    plotsDetail: (
      <>
        <p>Uniform hazard spectra, hazard curves, and site-amplification plots are provided below for the NBC and additional probabilities. By default, only the probabilities referred to in the NBC are displayed, others can be enabled/disabled by selecting the respective item in the legend. Curves representing intermediate points are determined through either linear-linear or log-log interpolation dependent on the scale selected. For convenience, PGA is plotted on the same scale. For low hazard regions, values below 0.001g for T &le; 2.0s and 0.0001g for T &gt; 2.0s are not shown.</p>
        <details>
          <summary>Instructions</summary>
          <table className="table bordered">
            <tbody>
              <tr>
                <th>Top buttons</th>
                <td>
                  <ol>
                    <li>Download the plot as an image in PNG format</li>
                    <li>Restore to the full area of the plot</li>
                    <li>Toggle the scale and intermediate point interpolation between linear and logarithmic (base 10)</li>
                  </ol>
                </td>
              </tr>
              <tr>
                <th>Legend</th>
                <td>
                  Turn the plotting of individual curves on or off by clicking on available parameters. Hovering over an individual parameter will highlight the corresponding curve on the plot (with larger circles at the curve's data points).
                </td>
              </tr>
              <tr>
                <th>Horizontal zoom bar</th>
                <td>
                  Users can zoom in on the horizontal scale by grabbing the ends of the bar below the plot and dragging it to the desired location. Users can return to the full plot by selecting the <em>restore area zoom</em> at the top of the plot.
                </td>
              </tr>
              <tr>
                <th>Values on the plot</th>
                <td>
                  Hovering over the plot will bring up vertical and horizontal lines indicating the x and y values of the point on the plot.
                </td>
              </tr>
            </tbody>
          </table>
        </details>
      </>
    ),
    maps: 'Maps',
    api: 'API',
    information: 'Background Information',
  },
  leaflet: {
    url: "https://www.earthquakescanada.nrcan.gc.ca/server2_serveur2/rest/services/BaseMaps/CBMT3978/MapServer/tile/{z}/{y}/{x}",
    attribution: "&#169; His Majesty the King in Right of Canada, as represented by the Minister of Natural Resources",
  },
};

export const initialStateFrench: LanguageState = {
  abbr: 'fr',
  title: 'Valeurs d\'aléas sismiques',
  codeMap: {
    'nbc2020': 'CNB 2020',
    'canSHM6': 'CNB 2020',
    'nbc2025': 'CNB 2025',
    'canSHM6P1': 'CNB 2025',
  },
  modelMap: {
    'nbc2020': 'CanadaSHM6-v1',
    'canSHM6': 'CanadaSHM6',
    'nbc2025': 'CanadaSHM6-v1',
    'canSHM6P1': 'CanadaSHM6-v1',
  },
  modelNotice: <>Les éditions antérieures sont disponibles dans <a href="/hazard-alea/interpolat/calc-fr.php">la calculatrice d\'aléas sismiques 2015-2005</a>.</>,
  goback: <>Retourner au <a href={window.location.pathname}>formulaire de calcul des valeurs d\'aléas sismiques</a></>,
  feedback: {
    error: 'Erreur!',
    errorDetails: 'Une erreur s\'est produite lors du chargement des résultats. Rafraîchir votre navigateur Web et si le problème persiste, veuillez nous en informer.',
    loading: 'Chargement...',
    loadingDetails: 'Nous chargeons les résultats, veuillez patienter.',
    empty: 'Aucun modèle d\'aléas sismiques trouvé.',
    emptyDetails: 'Vos coordonnées sélectionnées sont à l\'extérieur du Canada. Veuillez réviser vos coordonnées pour être au Canada et réessayer.',
  },
  form: {
    help: 'Aide',
    currentLocation: 'Estimer ma position actuelle',
    address: 'Adresse',
    searchAddress: 'Recherche l\'addresse',
    setCoordinates: 'Définissez les coordonnées',
    code: 'Édition du code',
    coordinates: 'Emplacement',
    coordinatesAddress: 'Adresse',
    coordinatesAddressHelp: 'ex : 275 rue Notre-Dame Est, Montréal, Québec',
    coordinatesCurrent: 'Emplacement actuel',
    coordinatesManual: 'Latitude et Longitude',
    coordinatesInstructions: (
      <details>
        <summary>Instructions</summary>
        <p>Les valeurs d\'aléas sismiques sont calculées pour des coordonnées géographiques spécifiques. Cet outil peut vous aider à déterminer les coordonnées de votre emplacement en utilisant l'une des options suivantes.</p>
        <ul>
          <li><strong>Adresse</strong>: Adresse civique du site</li>
          <li><strong>Emplacement actuel</strong>: Votre emplacement actuel peut être estimé à l'aide des données de géolocalisation de votre navigateur</li>
          <li><strong>Manuelle</strong>: Latitude (entre 42 et 90) et longitude (entre -141 et -45) du site en degrés décimaux</li>
        </ul>
        <p>Une fois le bouton de recherche sélectionné, une carte indiquant l'emplacement choisi par l'utilisateur apparaîtra. L'utilisateur peut ajuster l'emplacement sur la carte au besoin.</p>
      </details>
    ),
    coodinatesManualInstructions: 'Latitude (entre 42 et 90) et longitude (entre -141 et -45) du site en degrés décimaux, avec un maximum de 3 décimales.',
    siteDesignation: 'Désignation d\'emplacement',
    siteDesignationHelpMap: {
      'nbc2020': <>Choisissez la désignation d\'emplacement conformément à l\'article 4.1.8.4. du CNB 2020. Des directives additionnelles sont disponibles dans le commentaire intitulé <em>Calcul fondé sur les effets dus aux séismes</em> du <em>Commentaires sur le calcul des structures (Guide de l'utilisateur - CNB 2020 : Partie 4 de la division B)</em>.</>,
      'nbc2025': <>Choisissez la désignation d\'emplacement conformément à l\'article 4.1.8.4. du CNB 2025. Des directives additionnelles sont disponibles dans le commentaire intitulé <em>Calcul fondé sur les effets dus aux séismes</em> du <em>Commentaires sur le calcul des structures (Guide de l'utilisateur - CNB 2025 : Partie 4 de la division B)</em>.</>,
    },
    mapConfirm: 'Veuillez confirmer l\'emplacement. Vous pouvez ajuster manuellement l\'emplacement en déplaçant le marqueur.',
    submit: 'Obtenir les valeurs d\'aléas sismiques',
    beforeSubmit: 'Avant l\'envoi, assurez-vous de corriger les données suivantes :',
    selectedCoord: 'Les coordonnées sélectionnées sont',
    siteDesginationLabels: {
      XV: <>V<sub>s30</sub> (X<sub>V</sub>)</>,
      XS: <>Catégorie d'emplacement (X<sub>S</sub>)</>
    },
    // range of XV should match the one in AppState
    siteDesignationXV: <>V<sub>s30</sub> in situ entre 140 et 3000 <abbr title="mètres par seconde">m/s</abbr></>,
    siteDesignationXS: 'Catégorie d\'emplacement',
    error: {
      latitude: 'La latitude doit être comprise entre 42 et 90 degrés en degrés décimaux.',
      longitude: 'La longitude doit être comprise entre -141 et -45 degrés en degrés décimaux.',
      siteDesignationXS: 'Pour une désignation d\'emplacement Xs, une catégorie d\'emplacement doit être sélectionnée.',
      siteDesignationXV: 'Pour une désignation d\'emplacement Xv, la valeur Vs30 doit être comprise entre 140 et 3000 m/s',
      location: 'Impossible de trouver les coordonnées de l\'adresse. Veuillez corriger votre adresse ou utiliser l\'une des autres options.',
    }
  },
  summary: {
    details: 'Valeurs demandées par l\'utilisateur',
    model: 'Modèle',
    siteDesignationXV: <>Désignation d'emplacement X<sub>V</sub></>,
    siteDesignationXS: <>Désignation d'emplacement X<sub>S</sub></>,
  },
  chart: {
    uhs: 'Spectre uniforme de risque',
    hazardCurve: 'Courbe d\'aléa',
    vs30: 'Vs30 (m/s)',
    sa: 'Accélération spectrale (g)',
    gm: 'Réponse spectrale de l\'accélération \navec un amortissement de 5 % (g)',
    siteAmplification: 'Amplification locale',
    probability: 'Probabilité annuelle de dépassement',
    probabilitySelect: 'Probabilité',
    period: 'Période (s)',
    downloadImage: 'Télécharger le graphique sous forme d\'image (PNG)',
    zoom: 'zoom de la zone',
    back: 'zoom de la zone précédente',
    restore: 'Restaurer le graphique',
  },
  table: {
    all: 'Tous',
    siteDesignation: 'Désignation d\'emplacement',
    poe50: 'Probabilité',
    poe1: 'Probabilité',
    pga: 'PGA',
    pgv: 'PGV',
    download: 'Télécharger le fichier CSV',
    nbcTitle2: 'Probabilité de dépassement de 2 % en 50 ans (0,000404 par an)',
    nbcTitle5: 'Probabilité de dépassement de 5 % en 50 ans (0,001 par an)',
    nbcTitle10: 'Probabilité de dépassement de 10 % en 50 ans (0,0021 par an)',
    search: 'Rechercher',
    records: 'enregistrements',
  },
  tab: {
    intro: (
      <>
        <p><strong>Veuillez sélectionner l'un des onglets ci-dessous.</strong></p>
      </>
    ),
    additional: 'Valeurs additionnelles',
    tables: 'CanadaSHM',
    tablesDetailNBC: <p><strong>Les valeurs de réponse spectrale de l'accélération du CNB peuvent être consultées dans l'onglet CNB. Des valeurs additionnelles d'aléas pour votre emplacement se trouvent ci-dessous.</strong></p>,
    tablesDetail: (
      <>
        <p>Les valeurs de réponse spectrale de l'accélération avec un amortissement de 5 % (Sa(T), où T est la période, exprimée en secondes) et d'accélération maximale du sol (PGA) sont exprimées en unités d'accélération due à la gravité (g, soit 9,81 m/s2). Les valeurs de vitesse maximale du sol (PGV) sont exprimées en m/s. La probabilité est exprimée en pourcentage de dépassement en 50 ans.</p>
        <p>Toutes les probabilités pour la désignation d'emplacement précisée par l'utilisateur sont affichées par défaut. Les autres désignations d'emplacement peuvent être sélectionnées dans le menu déroulant correspondant du tableau. Dans les régions à faible aléa, une valeur minimale de 0,001g est attribuée pour T &le; 2,0s et de 0,0001g pour T &gt; 2,0 s. D'autres renseignements sur le calcul des aléas sismiques sont fournis sous l'onglet <em>Renseignements généraux</em>.</p>
      </>
    ),
    nbcDetailMap: {
      "nbc2020": (
        <>
          <p>Les valeurs de <u>réponse spectrale de l'accélération</u> avec un amortissement de 5 % (Sa(T,X), où T est la période, exprimée en secondes, et X la désignation d'emplacement) et d'<u>accélération maximale du sol</u> (PGA(X)) sont exprimées en unités d'accélération due à la gravité (g, soit 9,81 m/s<sup>2</sup>). Les valeurs de la <u>vitesse maximale du sol</u> (PGV(X)) sont exprimées en m/s. La probabilité est exprimée en pourcentage de dépassement en 50 ans. D'autres renseignements sur le calcul des valeurs d'aléas sismiques sont fournis sous l'onglet <em>Renseignements généraux</em>.</p>
          <p>Les valeurs d'aléas sismiques de 2 % en 50 ans sont fournies conformément à l'article 4.1.8.4. du CNB 2020. Les valeurs de 5 % et 10 % en 50 ans sont fournies aux fins de vérifications vérifications des exigences de performance supplémentaires conformément à l'article 4.1.8.23. du CNB 2020.</p>
          <p>Consultez l'onglet <em>Valeurs additionnelles</em> pour obtenir des valeurs additionnelles d'aléas sismiques, y compris des valeurs pour d'autres désignations d'emplacement, des périodes et des probabilités non définies dans le CNB 2020.</p>
        </>
      ),
      "nbc2025": (
        <>
          <p>Les valeurs de <u>réponse spectrale de l'accélération</u> avec un amortissement de 5 % (Sa(T,X), où T est la période, exprimée en secondes, et X la désignation d'emplacement) et d'<u>accélération maximale du sol</u> (PGA(X)) sont exprimées en unités d'accélération due à la gravité (g, soit 9,81 m/s<sup>2</sup>). Les valeurs de la <u>vitesse maximale du sol</u> (PGV(X)) sont exprimées en m/s. La probabilité est exprimée en pourcentage de dépassement en 50 ans. D'autres renseignements sur le calcul des valeurs d'aléas sismiques sont fournis sous l'onglet <em>Renseignements généraux</em>.</p>
          <p>Les valeurs d'aléas sismiques de 2 % en 50 ans sont fournies conformément à l'article 4.1.8.4. du CNB 2025. Les valeurs de 5 % et 10 % en 50 ans sont fournies aux fins de vérifications vérifications des exigences de performance supplémentaires conformément à l'article 4.1.8.23. du CNB 2025.</p>
          <p>Consultez l'onglet <em>Valeurs additionnelles</em> pour obtenir des valeurs additionnelles d'aléas sismiques, y compris des valeurs pour d'autres désignations d'emplacement, des périodes et des probabilités non définies dans le CNB 2025.</p>
        </>
      )
    },
    nbcCollapse: 'Tableaux des valeurs pour 5 % et 10 % en 50 ans',
    plots: 'Graphiques',
    plotsDetail: (
      <>
        <p>Les spectres uniformes de risque, les courbes d'aléa et les tracés d'amplification locale sont fournis ci-dessous pour le CNB et les probabilités additionnelles. Seules les probabilités mentionnées dans le CNB sont affichées par défaut, les autres peuvent être activées ou désactivées en sélectionnant l'élément respectif dans la légende. Les courbes représentant des points intermédiaires sont établies par interpolation bilinéaire ou bi-logarithmique en fonction de l'échelle sélectionnée. Pour des raisons de commodité, la PGA est représentée sur la même échelle. Pour les régions à faible aléa, les valeurs inférieures à 0,001g pour T &le; 2,0s et 0,0001g pour T &ge; 2,0s ne sont pas représentées.</p>
        <details>
          <summary>Instructions</summary>
          <table className="table bordered">
            <tbody>
              <tr>
                <th>Boutons supérieurs</th>
                <td>
                  <ol>
                    <li>Télécharger le graphique sous forme d'image (PNG)</li>
                    <li>Restaurer le graphique à pleine grandeur</li>
                    <li>Basculer l'échelle et l'interpolation des points intermédiaires entre linéaire et logarithmique (base 10)</li>
                  </ol>
                </td>
              </tr>
              <tr>
                <th>Légende</th>
                <td>
                  Activez ou désactivez le tracé des courbes individuelles en cliquant sur les paramètres disponibles. En passant la souris sur un paramètre individuel, la courbe correspondante sera mise en évidence dans le graphique (à l'aide de cercles plus grands aux points de données de la courbe).
                </td>
              </tr>
              <tr>
                <th>Barre de zoom horizontal</th>
                <td>
                  Les utilisateurs peuvent agrandir l'échelle horizontale en saisissant les extrémités de la barre située sous le graphique et en la faisant glisser jusqu'à l'emplacement souhaité. Les utilisateurs peuvent revenir à l'affichage à pleine grandeur en sélectionnant le bouton <em>Restaurer le graphique</em> situé en haut du graphique.
                </td>
              </tr>
              <tr>
                <th>Valeurs sur le graphique</th>
                <td>
                  Passer la souris sur le graphique fera apparaître des lignes verticales et horizontales indiquant les valeurs x et y du point sur le graphique.
                </td>
              </tr>
            </tbody>
          </table>
        </details>
      </>
    ),
    maps: 'Cartes',
    api: 'API',
    information: 'Renseignements généraux',
  },
  leaflet: {
    url: "https://www.earthquakescanada.nrcan.gc.ca/server2_serveur2/rest/services/BaseMaps/CBCT3978/MapServer/tile/{z}/{y}/{x}",
    attribution: "&#169; Sa Majesté la Reine du Chef du Canada, représentée par le ministre des Ressources naturelles",
  },
}

/**
 * Reducer settings
 * ----------------
 */
type ActionSetLanguage = {
  type: 'SET_LANGUAGE',
  payload: 'en' | 'fr',
}

type LanguageStateAction = ActionSetLanguage;

const reducer = (
  state: LanguageState,
  action: LanguageStateAction,
) => {
  switch (action.type) {
    case 'SET_LANGUAGE':
      switch (action.payload) {
        case 'fr':
          return initialStateFrench;
        default:
          return initialStateEnglish;
      }
    default:
      return state;
  }
}

/**
 * React useContext hook
 * ---------------------
 */
type LanguageContextProps = [
  LanguageState,
  React.Dispatch<LanguageStateAction>,
]

export const LanguageContext = createContext<LanguageContextProps>([initialStateEnglish, () => { }]);

type LanguageStateProviderProps = {
  initialState: LanguageState,
  children: React.ReactNode,
}

export const LanguageStateProvider = ({
  initialState,
  children,
}: LanguageStateProviderProps) => (
  <LanguageContext.Provider value={useReducer(reducer, initialState)}>
    {children}
  </LanguageContext.Provider>
);

export const useLanguageState = () => useContext(LanguageContext);
